// Pulling a chart's table from a URL (TODO §15/§5.2).
//
// === THE CORS WALL, said out loud ==========================================================
//
// A browser page **cannot** fetch an arbitrary remote CSV or JSON unless that host returns
// `Access-Control-Allow-Origin`. Most data hosts do not. This is not a bug to work around and
// not a thing a better fetch call fixes — it is the browser refusing on the user's behalf, and
// the failure it produces is deliberately opaque: a `TypeError: Failed to fetch` that says
// nothing about why, and is indistinguishable from the machine being offline.
//
// It is the same wall the AI terminal hit ([[ai-terminal-localhost-constraint]]), and it has
// the same escape hatch — a process with no CORS policy:
//
//   • **Desktop (§5.2.2)** — the main process fetches. It is Node, it has no origin, and it
//     answers to nobody's CORS headers. This is the only transport for which "any URL works"
//     is a true sentence, and the shell gates it (`electron/netFetch.ts`): http(s) only, no
//     redirect to a private or loopback address, a size cap and a timeout.
//   • **Web, direct (§5.2.3)** — plain `fetch`. Works for hosts that allow browser access,
//     which in practice means a published Google Sheet and APIs that were built to be called
//     from a page.
//   • **Web, proxied (§5.2.4)** — a Firebase callable, and **deliberately not built**. §12's
//     second open decision recommends deferring it, and the reason is worth keeping written
//     down: a server that fetches a URL a user typed carries real SSRF responsibility — it has
//     to block private and link-local ranges, refuse redirects into them, cap size and time,
//     require auth and rate-limit — for a feature that mostly matters on desktop, where the
//     shell already solves it. If it is ever built, `fetchRemoteTable` is the only function
//     that changes.
//
// §5.2.5 The capability is DETECTED AND LABELLED at the moment a URL is pasted, never
// discovered later at the bottom of an error. `describeTransport` is what the panel says.

import { parseCsvText, parseJsonTable, type ParsedTable } from "@/lib/chartCsv";
import { desktopShell } from "@/lib/desktopBridge";
import type { ChartSource } from "@/types/chart";

// Ten seconds. A slide's data source that has not answered in ten seconds is not going to
// answer usefully, and the editor has last-known-good rows on screen the whole time (§5.4.1).
const FETCH_TIMEOUT_MS = 10_000;

// 8 MB. Above the row cap's worth of CSV by a wide margin, and low enough that a URL pointed
// at something enormous by accident fails fast instead of filling the tab's memory.
export const FETCH_MAX_BYTES = 8 * 1024 * 1024;

export type ChartTransport = "desktop" | "direct";

export function currentTransport(): ChartTransport {
  return desktopShell()?.fetchRemoteText ? "desktop" : "direct";
}

// §5.2.5 One sentence for the panel, at the moment the URL is typed.
export function describeTransport(): string {
  return currentTransport() === "desktop"
    ? "Live URLs are fetched by the desktop app, so any address works."
    : "On the web, only sources that allow browser access can refresh — a published Google Sheets link, for example. The desktop app can fetch any address.";
}

// A `TypeError` from `fetch` is what the browser gives for a CORS refusal, a DNS failure, a
// dead network and a mixed-content block alike, so the message has to cover the ground rather
// than assert one cause. **What it must not say is "network error"** (§5.2.3): the network is
// usually fine, and sending someone to check their wifi over a CORS header is the exact wrong
// direction. The two real options come with it, because otherwise there is nothing to do next.
const OPAQUE_FETCH_MESSAGE =
  "The browser could not read this address. Most servers do not allow a web page to fetch " +
  "their files (a CORS restriction), and the browser does not say which happened. Two things " +
  "work: a source that allows browser access — a published Google Sheets CSV link, say — or " +
  "the desktop app, which fetches without that restriction.";

// Raw text from a URL, by whichever transport this runtime has.
async function fetchText(url: string): Promise<string> {
  const shell = desktopShell();
  if (shell?.fetchRemoteText) {
    // §5.2.2 The main process. Its errors are already sentences a person can act on — it knows
    // the status code, the size and whether it timed out — so they pass through untouched.
    const result = await shell.fetchRemoteText(url, {
      timeoutMs: FETCH_TIMEOUT_MS,
      maxBytes: FETCH_MAX_BYTES,
    });
    if (!result.ok) throw new Error(result.error);
    return result.text;
  }

  // §5.2.3 The web path. `AbortSignal.timeout` rather than a manual controller: it is the one
  // form that also covers a response whose BODY stalls after the headers arrive, which is how
  // a slow source actually fails.
  let res: Response;
  try {
    res = await fetch(url, {
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      // No credentials, ever: a data URL is not a place to send this origin's cookies, and
      // `include` would additionally make the CORS requirement stricter.
      credentials: "omit",
      redirect: "follow",
    });
  } catch (err) {
    // A timeout IS distinguishable and worth saying, because it is the one case where the
    // advice above is wrong — the server did answer the request, just too slowly.
    if (err instanceof DOMException && err.name === "TimeoutError") {
      throw new Error("The source did not answer within 10 seconds.");
    }
    throw new Error(OPAQUE_FETCH_MESSAGE);
  }
  if (!res.ok) {
    // A status code means CORS was satisfied and the server answered — a completely different
    // problem from the one above, and one the user can actually fix by checking the link.
    throw new Error(`The source answered ${res.status} ${res.statusText}.`);
  }
  const length = Number(res.headers.get("content-length") ?? 0);
  if (length > FETCH_MAX_BYTES) {
    throw new Error(
      `That file is ${Math.round(length / 1024 / 1024)} MB — too large for a slide chart.`,
    );
  }
  const text = await res.text();
  if (text.length > FETCH_MAX_BYTES) {
    throw new Error("That file is too large for a slide chart.");
  }
  return text;
}

// §5.2.1 The one seam. Everything above is transport; this is the only thing the rest of the
// app calls, and it always returns a parsed table or throws a message fit to show a user.
export async function fetchRemoteTable(
  source: Extract<ChartSource, { kind: "url" }>,
): Promise<ParsedTable> {
  const text = await fetchText(source.url);
  if (source.format === "json") {
    let payload: unknown;
    try {
      payload = JSON.parse(text);
    } catch {
      throw new Error("The response is not valid JSON.");
    }
    return parseJsonTable(payload, source.jsonPath);
  }
  return parseCsvText(text);
}

// A guessed format from the URL, so pasting a `.json` link does not also require choosing a
// format from a dropdown. A guess, not a rule — the panel shows it and it can be changed.
export function guessFormat(url: string): "csv" | "json" {
  return /\.json(\?|#|$)/i.test(url) ? "json" : "csv";
}

// §5.2.5 Is this a URL we can even attempt? Checked when it is typed rather than when it is
// fetched, so an obvious mistake is caught in front of the person who made it.
export function validateSourceUrl(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return "That is not a complete address — it needs to start with https://";
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return "Only http and https addresses can be used as a data source.";
  }
  // The web build is served over https, so an http source is blocked as mixed content before
  // any of the above matters. Say so here rather than letting it surface as the opaque error.
  if (
    url.protocol === "http:" &&
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    currentTransport() === "direct"
  ) {
    return "An http:// address cannot be read from this page, which is served over https. Use an https link, or the desktop app.";
  }
  return null;
}
